import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { DenikZaznam } from '../api/types.js';

/**
 * Měsíční kalendář deníku (kancelář) — úplnost na jeden pohled: podepsané dny
 * zelené, nepodepsané žluté, DÍRY (pracovní den bez záznamu mezi prvním
 * záznamem a dneškem) červeně — deník s dírami je právní riziko a v seznamu
 * nejsou vidět. Klik na den se záznamem ho otevře v čtečce.
 */

const DNY_TYDNE = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
const MESICE = [
  'leden',
  'únor',
  'březen',
  'duben',
  'květen',
  'červen',
  'červenec',
  'srpen',
  'září',
  'říjen',
  'listopad',
  'prosinec',
];

const denKlic = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function KalendarDeniku({
  dny,
  vybranyId,
  onVybrat,
}: {
  dny: DenikZaznam[];
  vybranyId?: string | null;
  onVybrat: (id: string) => void;
}) {
  const dnes = new Date();
  const [rok, setRok] = useState(dnes.getFullYear());
  const [mesic, setMesic] = useState(dnes.getMonth());

  // Klíč = datum z ISO řetězce (YYYY-MM-DD) — přes new Date() by se den
  // posunul podle časové zóny (záznamy jsou UTC půlnoci).
  const zaznamy = new Map<string, DenikZaznam>();
  let prvniKlic: string | null = null;
  for (const d of dny) {
    const klic = String(d.datum).slice(0, 10);
    zaznamy.set(klic, d);
    if (!prvniKlic || klic < prvniKlic) prvniKlic = klic;
  }

  const prvniDenMesice = new Date(rok, mesic, 1);
  const dniVMesici = new Date(rok, mesic + 1, 0).getDate();
  // Po = 0 … Ne = 6 (getDay: Ne = 0).
  const posunZacatku = (prvniDenMesice.getDay() + 6) % 7;

  const posun = (smer: number) => {
    const d = new Date(rok, mesic + smer, 1);
    setRok(d.getFullYear());
    setMesic(d.getMonth());
  };

  const bunky: { den: number; datum: Date }[] = [];
  for (let i = 1; i <= dniVMesici; i++) bunky.push({ den: i, datum: new Date(rok, mesic, i) });

  return (
    <section className="rounded-xl border border-border bg-surface p-3">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          aria-label="Předchozí měsíc"
          className="flex size-8 items-center justify-center rounded-md text-text-secondary hover:bg-bg"
          onClick={() => posun(-1)}
        >
          <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
        </button>
        <p className="text-sm font-bold">
          {MESICE[mesic]} {rok}
        </p>
        <button
          type="button"
          aria-label="Další měsíc"
          className="flex size-8 items-center justify-center rounded-md text-text-secondary hover:bg-bg"
          onClick={() => posun(1)}
        >
          <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {DNY_TYDNE.map((d) => (
          <span key={d} className="text-[11px] font-semibold text-text-secondary">
            {d}
          </span>
        ))}
        {Array.from({ length: posunZacatku }, (_, i) => (
          <span key={`p${i}`} />
        ))}
        {bunky.map(({ den, datum }) => {
          const zaznam = zaznamy.get(denKlic(datum));
          const vikend = datum.getDay() === 0 || datum.getDay() === 6;
          const budoucnost = datum > dnes;
          const jeDnes = denKlic(datum) === denKlic(dnes);
          const dira =
            !zaznam && !vikend && !budoucnost && prvniKlic !== null && denKlic(datum) >= prvniKlic;

          const vzhled = zaznam
            ? zaznam.nepodepsany
              ? 'bg-warning-bg text-warning-text font-semibold hover:ring-2 hover:ring-warning-border'
              : 'bg-success-bg text-success-text font-semibold hover:ring-2 hover:ring-success-border'
            : dira
              ? 'bg-error-bg text-error-text font-semibold'
              : 'text-text-muted';

          const spolecne = `relative flex h-9 items-center justify-center rounded-md text-sm tabular-nums ${vzhled} ${
            zaznam && zaznam.id === vybranyId ? 'ring-2 ring-primary' : ''
          }`;

          const titulek = zaznam
            ? zaznam.nepodepsany
              ? 'Nepodepsaný záznam'
              : 'Podepsaný záznam'
            : dira
              ? 'DÍRA — pracovní den bez záznamu'
              : undefined;

          return zaznam ? (
            <button
              key={den}
              type="button"
              title={titulek}
              aria-label={`${den}. ${MESICE[mesic]} — ${titulek ?? ''}`}
              className={spolecne}
              onClick={() => onVybrat(zaznam.id)}
            >
              {den}
              {jeDnes && (
                <span
                  aria-hidden="true"
                  className="absolute right-1 bottom-1 size-1.5 rounded-full bg-primary"
                />
              )}
            </button>
          ) : (
            <span
              key={den}
              title={titulek}
              aria-label={dira ? `${den}. ${MESICE[mesic]} — díra v deníku` : undefined}
              className={spolecne}
            >
              {den}
            </span>
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-text-secondary">
        <span className="flex items-center gap-1">
          <span className="size-2.5 rounded-sm bg-success-bg ring-1 ring-success-border" />{' '}
          podepsáno
        </span>
        <span className="flex items-center gap-1">
          <span className="size-2.5 rounded-sm bg-warning-bg ring-1 ring-warning-border" />{' '}
          nepodepsáno
        </span>
        <span className="flex items-center gap-1">
          <span className="size-2.5 rounded-sm bg-error-bg ring-1 ring-error-border" /> díra (bez
          záznamu)
        </span>
      </div>
    </section>
  );
}
